import commonservice from "@apis/commonservice";
import { fetchAssetState } from "@apis/http/api";
import message from "@utils/message";
import { useClosableMessage } from "@hooks/useClosableMessage";
import { notifyGeneratedVideoAssetsChanged } from "@pages/library/hooks/useGeneratedVideoAssets";
import report from "@report";
import classNames from "classnames";
import { useSerializeCallback } from "@kalodata/tools";

export const DownloadVideo = ({
  videoURL: OriginalVideoURL,
  videoId,
  videoSource,
  onDownload,
  children,
  className,
  disabled,
  kalo_type,
}: {
  videoURL?: string;
  videoId: string;
  videoSource?: string;
  onDownload?: () => void;
  children: React.ReactNode | ((isDownloading: boolean) => React.ReactNode);
  className?: string;
  disabled?: boolean;
  kalo_type?: string;
}) => {
  const { showClosableMessage } = useClosableMessage();

  const checkAssetState = async (): Promise<boolean> => {
    try {
      const res = await fetchAssetState({ assetId: Number(videoId) } as any);
      const code = (res as any)?.data?.code ?? (res as any)?.code;
      if (code === 0 || code === -1) {
        message.error("Video deleted. Unable to download.");
        notifyGeneratedVideoAssetsChanged();
        return false;
      } else if (code === -2) {
        message.error("Credits refunded and video deleted. Unable to download.");
        notifyGeneratedVideoAssetsChanged();
        return false;
      }
      return true;
    } catch {
      message.error("Failed to verify asset state.");
      return false;
    }
  };

  const downloadVideo = useSerializeCallback(async () => {
    report.event("download_video", { kalo_type, kalo_value: videoId });

    try {
      if (OriginalVideoURL) {
        const allowed = await checkAssetState();
        if (!allowed) return;
        const link = document.createElement("a");
        link.href = OriginalVideoURL + "?download=1";
        link.download = `${videoId}.mp4`;
        document.body.appendChild(link);
        link.click();
      } else {
        const videoURL = await commonservice.getDownloadVideoURL({ videoId: videoId, videoSource: videoSource || "" });
        if (videoURL) {
          const link = document.createElement("a");
          link.href = videoURL as string;
          link.download = `${videoId}.mp4`;
          document.body.appendChild(link);
          link.click();
        } else {
          message.error("Server error");
          return;
        }
      }
      onDownload?.();
    } catch (error: any) {
      if (error?.message?.includes("Upgrade") >= 0) {
        showClosableMessage({
          content: "Your download limit has been reached. Please go to the pricing page to purchase the recharge plan.",
          duration: 5,
        });
      } else {
        message.error("Server error");
      }
    }
  });
  const isDownloading = downloadVideo.isPending();

  return (
    <div
      className={classNames("cursor-pointer relative", className)}
      onClick={() => {
        if (disabled || isDownloading) return;
        downloadVideo();
      }}
    >
      {typeof children === "function" ? children(isDownloading) : children}
    </div>
  );
};
